"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.jsPsych = exports.setNextStimulus = exports.moveToNextBlock = exports.initializeClowder = exports.initializeCat = exports.seenCatItemsCount = exports.scaleTheta = exports.clowder = exports.cat = exports.isTouchScreen = void 0;
//@ts-ignore
const roar_utils_1 = require("@bdelab/roar-utils");
const jscat_1 = require("@bdelab/jscat");
const jspsych_1 = require("jspsych");
require("../i18n/i18n");
const taskStore_1 = require("../taskStore");
const utils_1 = require("../utils");
const clowderSetup_1 = require("./shared/helpers/clowderSetup");
exports.isTouchScreen = (0, roar_utils_1.getDevice)() === 'mobile';
exports.seenCatItemsCount = {};
const { runCat } = (0, taskStore_1.taskStore)();
const initializeCat = () => {
    exports.cat = new jscat_1.Cat({
        method: 'MLE',
        minTheta: -6,
        maxTheta: 6,
        theta: (0, taskStore_1.taskStore)().startingTheta || 0,
        itemSelect: (0, taskStore_1.taskStore)().itemSelect,
    });
};
exports.initializeCat = initializeCat;
const initializeClowder = async (config) => {
    var _a, _b, _c, _d, _e;
    if (!config) {
        throw new Error('Config is not found');
    }
    const { task, scoringVersion, corpora, randomSeed } = (0, taskStore_1.taskStore)();
    const clowderConfig = (_a = clowderSetup_1.CLOWDER_CONFIG[task]) === null || _a === void 0 ? void 0 : _a[scoringVersion];
    const clowderSelectionConfig = (_b = clowderSetup_1.CLOWDER_SELECTION_CONFIG[task]) === null || _b === void 0 ? void 0 : _b[scoringVersion];
    if (!clowderConfig) {
        throw new Error('Clowder config is not found');
    }
    for (const corpusToSelectFrom of Object.values(clowderSelectionConfig.catOrderMap)) {
        exports.seenCatItemsCount[corpusToSelectFrom] = 0;
    }
    const { csvUrl } = (_c = clowderSetup_1.CLOWDER_IRT_HYPERPARAMS[task]) === null || _c === void 0 ? void 0 : _c[scoringVersion];
    if (!csvUrl) {
        throw new Error('IRT hyperparameters CSV URL is not found');
    }
    const irtHyperparams = await (0, clowderSetup_1.getIrtHyperparameters)(csvUrl);
    exports.scaleTheta = (0, clowderSetup_1.createScaleTheta)(irtHyperparams);
    const baseConfig = {
        theta: (0, taskStore_1.taskStore)().startingTheta,
        // Defaults to what letter uses
        method: (_e = (_d = clowderConfig.method) === null || _d === void 0 ? void 0 : _d.toLowerCase()) !== null && _e !== void 0 ? _e : 'eap',
    };
    const practiceConfig = {
        ...baseConfig,
        priorDist: clowderConfig.priorDist,
        // Fixed after ordering by cat zetas
        itemSelect: 'fixed',
    };
    const blockConfigs = Object.fromEntries(clowderSelectionConfig.catsToUpdate.map((catName) => [
        catName,
        {
            ...baseConfig,
            ...clowderConfig,
            // When catName (trial_type) is not found in irtHyperparams, use composite for params other than scale & transformation
            ...(irtHyperparams[catName] ? irtHyperparams[catName] : irtHyperparams['composite']),
            // Pass randomSeed if we want to show the same randomized order of items.
            ...(randomSeed != null ? { randomSeed } : {}),
        },
    ]));
    const catConfig = {
        practice: practiceConfig,
        ...blockConfigs,
    };
    const earlyStoppingCats = new jscat_1.StopAfterNItems({
        ...clowderSetup_1.CLOWDER_EARLY_STOPPING_RULES[task][scoringVersion],
    });
    const { catNames, delimiter } = clowderSetup_1.CLOWDER_CORPUS_PARAMS[task][scoringVersion];
    const clowderCorpus = await (0, jscat_1.prepareClowderCorpus)(corpora.stimulus, catNames, delimiter);
    exports.clowder = new jscat_1.Clowder({
        cats: catConfig,
        corpus: clowderCorpus,
        earlyStopping: earlyStoppingCats,
        randomSeed,
    });
};
exports.initializeClowder = initializeClowder;
const moveToNextBlock = (customIndex) => {
    const { currentCatBlock, task, scoringVersion } = (0, taskStore_1.taskStore)();
    const { catOrderMap } = clowderSetup_1.CLOWDER_SELECTION_CONFIG[task][scoringVersion];
    if (customIndex != undefined && customIndex >= 0) {
        if (!catOrderMap[customIndex]) {
            throw new Error('Cannot set to block not in catOrderMap');
        }
        (0, taskStore_1.taskStore)('currentCatBlock', customIndex);
        return;
    }
    // Default to moving to the next block
    (0, taskStore_1.taskStore)('currentCatBlock', (currentCatBlock !== null && currentCatBlock !== void 0 ? currentCatBlock : -1) + 1);
};
exports.moveToNextBlock = moveToNextBlock;
const setNextStimulus = ({ ignorePreviousItem = false, additionalItemsToRemove = [] } = {}) => {
    const { task, currentCatBlock, scoringVersion, previousItem, previousAnswer } = (0, taskStore_1.taskStore)();
    const { catOrderMap, catsToUpdate: taskCatsToUpdate } = clowderSetup_1.CLOWDER_SELECTION_CONFIG[task][scoringVersion];
    const safeCatBlock = currentCatBlock !== null && currentCatBlock !== void 0 ? currentCatBlock : 0;
    const corpusToSelectFrom = catOrderMap[safeCatBlock];
    const catsToUpdate = corpusToSelectFrom.includes('practice') ? [corpusToSelectFrom] : taskCatsToUpdate;
    const nextStimulus = exports.clowder.updateCatAndGetNextItem({
        catToSelect: corpusToSelectFrom, // The Cat instance to use for selecting the next stimulus.
        corpusToSelectFrom, // The "block" to use for selecting the next stimulus. If not provided, `catToSelect` will be used.
        catsToUpdate, // A single Cat or array of Cats for which to update ability estimates.
        items: ignorePreviousItem ? undefined : previousItem,
        answers: ignorePreviousItem ? undefined : previousAnswer,
        additionalItemsToRemove,
        randomlySelectUnvalidated: false,
    });
    // eslint-disable-next-line eqeqeq
    if (nextStimulus == undefined) {
        // store2 considered undefined values as no change, so we need to use null
        (0, taskStore_1.taskStore)('nextStimulus', null);
        (0, exports.moveToNextBlock)();
    }
    else {
        exports.seenCatItemsCount[corpusToSelectFrom] += 1;
        (0, taskStore_1.taskStore)('nextStimulus', nextStimulus);
    }
};
exports.setNextStimulus = setNextStimulus;
exports.jsPsych = (0, jspsych_1.initJsPsych)({
    on_data_update: function (data) {
        // Removing stimulus from data to avoid sending large html files to Levante
        const { stimulus, task, ...rest } = data;
        const logger = utils_1.Logger.getInstance();
        // Avoid logging fixation trials
        if (task !== 'fixation') {
            logger.capture('JsPsych Data Update', rest);
        }
    },
});
window.initJsPsych = exports.jsPsych;
